import { ArrowUpFromLine } from "lucide-react";
import React from "react";

interface ImageTabProps {
    selectedImage: string | null;
    handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    setIsDragging: (dragging: boolean) => void;
    isDragging: boolean;
    selectedLocation: string;
    loadingLocations: boolean;
    locations: Array<{ id: number; name: string; type: string }>;
    setSelectedLocation: React.Dispatch<React.SetStateAction<string>>;
    handleReset: () => void;
    isActionDisabled: boolean;
}

const ImageTab: React.FC<ImageTabProps> = ({
    selectedImage,
    handleFileUpload,
    setIsDragging,
    isDragging,
    selectedLocation,
    loadingLocations,
    locations,
    setSelectedLocation,
    handleReset,
    isActionDisabled,
}) => {
    return (
        <div>
            {/* Location Dropdown */}
            <div className="flex justify-center gap-4 mb-4">
                <select
                    className="px-4 py-2 rounded border border-gray-300 bg-white shadow"
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    disabled={loadingLocations}
                >
                    {loadingLocations ? (
                        <option value="">Loading locations...</option>
                    ) : locations.length > 0 ? (
                        locations.map((loc) => (
                            <option key={loc.id} value={loc.name}>
                                {loc.name} ({loc.type})
                            </option>
                        ))
                    ) : (
                        <option value="">No locations available</option>
                    )}
                </select>
            </div>

            {/* Upload Area */}
            <div
                className={`border-2 border-dashed border-gray-300 rounded-xl p-12 text-center relative transition-colors duration-200 ${isDragging ? "bg-gray-300" : "hover:bg-gray-300"
                    }`}
                onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file && file.type.startsWith("image/")) {
                        const reader = new FileReader();
                        reader.onloadend = () => handleFileUpload({
                            target: { files: [file] }
                        } as unknown as React.ChangeEvent<HTMLInputElement>);
                        reader.readAsDataURL(file);
                    } else {
                        alert(`Please upload a valid image file.`);
                    }
                }}
            >
                {selectedImage ? (
                    // Image preview
                    <img
                        src={selectedImage}
                        alt="Uploaded Preview"
                        className="mx-auto max-h-64 rounded-xl"
                    />
                ) : (
                    // Upload prompt
                    <>
                        <div className="flex justify-center mb-4">
                            <div className="bg-gray-100 rounded-full p-4">
                                <ArrowUpFromLine className="text-gray-400" size={48} />
                            </div>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Upload Image</h3>
                        <p className="text-gray-600 mb-2">
                            Drag and drop an image here, or click to select a file
                        </p>
                        <p className="text-sm text-gray-500 mb-6">Supported formats: JPG, PNG, JPEG</p>
                    </>
                )}

                <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileUpload}
                />
                <label htmlFor="file-upload" className="cursor-pointer absolute inset-0" />
            </div>

            {/* Reset Button */}
            <div className="mt-6 flex justify-center">
                <button
                    onClick={handleReset}
                    disabled={isActionDisabled}
                    className={`px-8 py-3 border rounded-lg font-medium transition-colors ${isActionDisabled
                            ? "border-gray-300 text-gray-400 cursor-not-allowed bg-gray-100"
                            : "border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                >
                    Reset
                </button>
            </div>
        </div>
    );
};

export default ImageTab;
